import { Link } from 'react-router-dom';
import * as AlertNotification from '@givebutter/primitives/alert-notification';
import { BoxIcon } from '@givebutter/primitives/box-icon';
import { Button, ButtonProps } from '@givebutter/primitives/button';
import { maskDecimal } from '@givebutter/util';
import { faSackDollar } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useOveragePosthogProperties } from 'assets/js/pages/account-settings/account-about/givebutter-plus/useOveragePosthogProperties';
import { usePlanOverageUsageData } from 'assets/js/pages/account-settings/account-about/givebutter-plus/usePlanOverageUsageData';
import { usePermissions } from 'components/permissions';
import { AccountPermission } from 'components/permissions/PermissionEnums';
import { AnchorHashIds } from 'constants/anchor-hash-ids';
import { posthogEvents } from 'constants/posthog-events';
import { CurrencySymbol } from 'customHooks/useCurrencySymbol';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { usePosthog } from 'customHooks/usePosthog';
import { useRecommendedPlanComparison } from 'customHooks/useRecommendedPlanComparison';
import { LONG_DATE_FORMAT, formatDate, isoDateFromServer } from 'helpers/dates';

const ImportContactsOveragesWarning = () => {
    const { isPermitted: canManagePlus } = usePermissions([AccountPermission.PLUS_MANAGEMENT]);
    const { accountId, validGivebutterPlusPlan } = useCurrentAccount();
    const { isRecommendedPlanCheaper, recommendedPlanDifference } = useRecommendedPlanComparison();
    const { over80, over90, hasOverages, isGracePeriod } = usePlanOverageUsageData();
    const overagePosthogProps = useOveragePosthogProperties();
    const { pushEvent } = usePosthog();

    if (!validGivebutterPlusPlan) {
        return null;
    }

    const { current_period_end: currentPeriodEnd, tier } = validGivebutterPlusPlan;

    if (!over80) {
        return null;
    }

    return (
        <AlertNotification.Root type="info" variant="solid">
            <BoxIcon size="md">
                <FontAwesomeIcon
                    icon={faSackDollar}
                    size="lg"
                    className="text-brand-cobalt-primary"
                />
            </BoxIcon>
            <div className="flex flex-col gap-2 text-sm">
                <div>
                    {!hasOverages && (
                        <>
                            <p className="font-medium">
                                You've reached {over90 ? '90%' : '80%'} of the contact limit of your
                                Givebutter Plus plan.
                            </p>
                            <p className="text-gray-600">
                                Consider updating to a higher-tier plan to avoid overage charges or
                                use data hygiene to clean up your contacts.
                            </p>
                        </>
                    )}
                    {hasOverages && isRecommendedPlanCheaper && (
                        <>
                            <p className="font-medium">
                                You could save <CurrencySymbol />
                                {maskDecimal(recommendedPlanDifference)}{' '}
                                {tier.interval === 'month' ? '/mo' : '/year'} in overages by
                                updating your Givebutter Plus plan.
                            </p>
                            <p className="text-gray-600">
                                {isGracePeriod && currentPeriodEnd
                                    ? `Overage charges will start on ${formatDate(isoDateFromServer(currentPeriodEnd), LONG_DATE_FORMAT)}. Consider updating to a higher-tier plan to avoid overage charges or use data hygiene to clean up your contacts.`
                                    : 'You’re spending more in overage charges than the cost of the recommended plan. Consider updating to a higher-tier plan to avoid overage charges or use data hygiene to clean up your contacts.'}
                            </p>
                        </>
                    )}
                    {hasOverages && !isRecommendedPlanCheaper && (
                        <>
                            <p className="font-medium">
                                You are over your contact limit for your Givebutter Plus plan and
                                will be charged overages.
                            </p>
                            <p className="text-gray-600">
                                {isGracePeriod &&
                                    currentPeriodEnd &&
                                    `Overage charges will start on ${formatDate(isoDateFromServer(currentPeriodEnd), LONG_DATE_FORMAT)}. `}
                                Consider updating to a higher-tier plan to avoid overage charges or
                                use data hygiene to clean up your contacts.
                            </p>
                        </>
                    )}
                </div>
                {canManagePlus ? (
                    <div className="flex gap-4 font-medium">
                        <DataHygieneButton />
                        <Link
                            to={`/accounts/${accountId}/settings/about/subscription#${AnchorHashIds.BillingViewGivebutterPlusPlan}`}
                        >
                            <Button
                                variant="none"
                                color="primary"
                                onClick={() => {
                                    pushEvent(
                                        posthogEvents.DASHBOARD_OVERAGES_IMPORTS_BANNER_SELECTED,
                                        {
                                            ...overagePosthogProps,
                                            text: 'Manage plan',
                                        },
                                    );
                                }}
                            >
                                Manage plan
                            </Button>
                        </Link>
                    </div>
                ) : (
                    <div className="flex gap-4 font-medium">
                        <DataHygieneButton color="primary" />
                        <p className="text-gray-700">Contact an admin</p>
                    </div>
                )}
            </div>
        </AlertNotification.Root>
    );
};

interface DataHygieneButtonProps extends Omit<ButtonProps, 'variant'> {}
const DataHygieneButton = ({ color, onClick, ...rest }: DataHygieneButtonProps) => {
    const { accountId } = useCurrentAccount();
    const overagePosthogProps = useOveragePosthogProperties();
    const { pushEvent } = usePosthog();

    return (
        <Link to={`/accounts/${accountId}/data-hygiene`}>
            <Button
                variant="none"
                color={color ?? 'neutral'}
                onClick={e => {
                    pushEvent(posthogEvents.DASHBOARD_OVERAGES_IMPORTS_BANNER_SELECTED, {
                        ...overagePosthogProps,
                        text: 'Data hygiene',
                    });
                    onClick?.(e);
                }}
                {...rest}
            >
                Data hygiene
            </Button>
        </Link>
    );
};

export { ImportContactsOveragesWarning };
